// src/oura/delivery/DeliveryList.tsx
//
// Delivery's list: one line per deliverable, grouped by who acts next (Needs
// us, Waiting on client, Checking, Done, Later). The line says the next step
// and who takes it; the counts live in the drawer head. With the Checklist on,
// each deliverable lists its items under it, each marked by its square.

import { useEffect, useState, type ReactNode } from "react";
import { CheckIcon, ChevronRightIcon, FileTextIcon, WrenchIcon } from "lucide-react";
import type { Deliverable, DeliveryFailure, DeliveryGroup, DeliveryItem } from "@service/aeo/delivery";
import { dayLabel, forceDeliverableDone, forceItemDone, reopenDeliverable, untickItem } from "@service/aeo/delivery";
import { workingDaysBetween } from "@service/aeo/clientDocsRules";
import { todayIso } from "../components/plan/planDates";
import { dueBands, dueOf, jobsOf, nextLine, type DeliveryGroups, type DueBand, type Job, type ListMode } from "./listModel";
import { cn } from "../lib/cn";
import { FamilyChip, GroupFold, IconSquare, ListRow, PillButton, Tag, TYPE } from "../ui";
import { familyOf } from "../components/plan/planModel";
import { GROUP_TITLE, GROUP_TONE, actorText, itemFact, plural, squareOf } from "./format";
import { Square } from "./Square";
import { FORCE_DONE_ON } from "./flag";
import { smallChangeLine } from "./RequestRows";
import type { ChangeRequest } from "@service/aeo/clientReview";

export function DeliveryList({
    businessId,
    groups,
    mode,
    openKey,
    onOpen,
    month,
    requests,
}: {
    businessId: string;
    groups: DeliveryGroups[];
    mode: ListMode;
    openKey: string | null;
    onOpen: (key: string, group: DeliveryGroup) => void;
    /** The current plan month: rows from other months say theirs. */
    month: number;
    /** The client's change requests: G5's line names the one filed as the month's small change. */
    requests?: readonly ChangeRequest[] | null;
}) {
    return (
        <div>
            {groups.map((g) => (
                <GroupFold
                    key={g.key}
                    id={`delivery.${businessId}.${g.key}`}
                    tone={GROUP_TONE[g.key]}
                    title={GROUP_TITLE[g.key]}
                    count={g.key === "needs_us" ? jobsOf(g.rows).length : g.rows.length}
                    tags={g.key === "needs_us" && jobsOf(g.rows).length !== g.rows.length ? <span className={cn(TYPE.tc, "text-gray-400")}>{g.rows.length} deliverables</span> : undefined}
                    lead="square"
                    defaultFolded={g.key === "checking" || g.key === "done" || g.key === "later"}
                    note={g.note}
                >
                    {g.key === "needs_us" ? (
                        <NeedsUsBands businessId={businessId} rows={g.rows} mode={mode} openKey={openKey} onOpen={(key) => onOpen(key, g.key)} month={month} requests={requests} />
                    ) : g.rows.map((d) =>
                        mode === "next" ? (
                            <NextRow key={d.key} d={d} selected={openKey === d.key} onOpen={() => onOpen(d.key, g.key)} month={month} requests={requests} />
                        ) : (
                            <ChecklistRow key={d.key} businessId={businessId} d={d} selected={openKey === d.key} onOpen={() => onOpen(d.key, g.key)} month={month} />
                        ),
                    )}
                </GroupFold>
            ))}
        </div>
    );
}

/**
 * Needs us in bands by due date. Bands that are late or due today start open;
 * a later date starts folded while anything is late, so the list opens on
 * what is overdue.
 */
function NeedsUsBands({
    businessId,
    rows,
    mode,
    openKey,
    onOpen,
    month,
    requests,
}: {
    businessId: string;
    rows: Deliverable[];
    mode: ListMode;
    openKey: string | null;
    onOpen: (key: string) => void;
    month: number;
    requests?: readonly ChangeRequest[] | null;
}) {
    const bands = dueBands(jobsOf(rows), month);
    const anyLate = bands.some((b) => b.tone === "late");
    return (
        <div className="space-y-1">
            {bands.map((b) => (
                <Band key={`${businessId}.${b.key}`} band={b} startFolded={anyLate && b.tone === "coming"} openKey={openKey}>
                    {b.jobs.map((j) =>
                        mode === "next" ? (
                            <JobRow key={j.key} job={j} owner={b.owner ? null : (j.rows[0].actor?.name ?? null)} selected={j.rows.some((d) => d.key === openKey)} onOpen={() => onOpen(j.rows[0].key)} requests={requests} />
                        ) : (
                            j.rows.map((d) => <ChecklistRow key={d.key} businessId={businessId} d={d} selected={openKey === d.key} onOpen={() => onOpen(d.key)} month={b.month ?? month} metaOverride={b.owner ? null : (d.actor?.name ?? null)} />)
                        ),
                    )}
                </Band>
            ))}
        </div>
    );
}

/** One thing to do; the codes it moves on the right, their names under it. */
function JobRow({ job, owner, selected, onOpen, requests }: { job: Job; owner: string | null; selected: boolean; onOpen: () => void; requests?: readonly ChangeRequest[] | null }) {
    const d = job.rows[0];
    const many = job.rows.length > 1;
    // G5 on its own names the request filed as the month's small change, as its row outside a band does.
    const small = many ? null : smallChangeLine(d, requests);
    return (
        <ListRow
            icon={<IconSquare tone={job.kind === "doc" ? "blue" : "gray"} icon={job.kind === "doc" ? FileTextIcon : WrenchIcon} />}
            lead="square"
            title={small ?? job.title}
            sub={
                <span className="text-gray-500">
                    {d.passedWithoutWork && (
                        <Tag tone="amber" className="mr-1.5">
                            No work logged
                        </Tag>
                    )}
                    {many ? `Moves ${job.rows.length}: ` : ""}
                    {job.rows.map((r) => r.label + (r.outlet ? `, ${r.outlet}` : "")).join(" · ")}
                </span>
            }
            tags={job.rows.map((r) => (
                <FamilyChip key={r.key} family={familyOf(r.code)}>
                    {r.code}
                </FamilyChip>
            ))}
            meta={owner ?? undefined}
            onClick={onOpen}
            stackMeta
            className={cn(selected && "bg-blue-50/60")}
            data-deliverable={d.key}
        />
    );
}

function Band({ band, startFolded, openKey, children }: { band: DueBand; startFolded: boolean; openKey: string | null; children: ReactNode }) {
    const [folded, setFolded] = useState(startFolded);
    // A deep link or the drawer's next arrow lands in a folded band: show the row it opened.
    const holdsOpen = openKey !== null && band.jobs.some((j) => j.rows.some((d) => d.key === openKey));
    useEffect(() => {
        if (holdsOpen) setFolded(false);
    }, [holdsOpen, openKey]);
    const facts = [band.month ? `Month ${band.month}` : null, band.owner].filter(Boolean).join(" · ");
    const codes = band.jobs.flatMap((j) => j.rows.map((d) => d.code)).join(" · ");
    return (
        <section>
            <button
                type="button"
                aria-expanded={!folded}
                onClick={() => setFolded(!folded)}
                className="flex min-h-9 w-full items-center gap-2 rounded-md bg-gray-50 px-3 text-left"
            >
                <ChevronRightIcon aria-hidden size={14} className={cn("shrink-0 text-gray-400 transition-transform", !folded && "rotate-90")} />
                <span title={band.due ? `Due ${dayLabel(band.due)}` : undefined} className={cn(TYPE.tc, "font-semibold", band.tone === "late" ? "text-rose-600" : band.tone === "proof" ? "text-amber-700" : "text-gray-900")}>{band.title}</span>
                <span className={cn(TYPE.tc, "tabular-nums text-gray-400")}>{band.jobs.length}</span>
                <span className={cn(TYPE.tc, "ml-auto min-w-0 truncate text-gray-500")}>{folded ? codes : facts}</span>
            </button>
            {!folded && <div className="pt-1">{children}</div>}
        </section>
    );
}

function CodeLead({ d }: { d: Deliverable }) {
    return <FamilyChip family={familyOf(d.code)}>{d.code}</FamilyChip>;
}

/** Month 1 leftovers and outlets say so; this month's rows need no tag. */
function rowTitle(d: Deliverable, month: number | null): string {
    const parts = [d.label];
    if (month !== null && d.month !== month) parts.push(`month ${d.month}`);
    return parts.join(", ");
}

/**
 * A late row of ours says how late, the way What to do next says it: "Angel ·
 * 25 days late", in working days past the due date. The date
 * itself is on hover. A red date with no word, or a client wait in days that
 * may or may not be late, had to be worked out (Jack, 22 Oct 2026).
 */
function lateText(d: Deliverable, who: string): ReactNode {
    const due = dueOf(d);
    if (!due) return who;
    const days = Math.max(1, workingDaysBetween(due, todayIso()));
    return <span title={`Due ${dayLabel(due)}`}>{`${who} · ${plural(days, "day")} late`}</span>;
}

function actorFact(d: Deliverable): { meta?: ReactNode; late?: ReactNode } {
    const a = d.actor;
    if (!a) {
        if (d.group === "done") return { meta: d.doneAt ? `Done ${dayLabel(d.doneAt)}` : "Done" };
        if (d.group === "later") {
            // Every item ticked, but the deliverable is not in the done group:
            // "Not started" beside a line that reads "1 of 1 done" is a
            // contradiction on one row (#3989). The count is the true half, so
            // the label is the half that goes — nothing here invents a state
            // the server did not report.
            const items = d.items ?? [];
            if (items.length > 0 && items.every((i) => i.state === "done")) return {};
            // A plan with no start date has windows with no dates (`phase: "not_started"`), so "From" alone is never printed.
            return { meta: d.window?.start ? `From ${dayLabel(d.window.start)}` : "Not started" };
        }
        return {};
    }
    const text = actorText(a);
    if (a.cameBack) return { late: text };
    // The client's late row keeps how long they have had it: its question has its own
    // late count (Overview, Client), and a second, different "N days late" would argue with it.
    if (a.late && a.who === "client") return { late: <span title={dueOf(d) ? `Due ${dayLabel(dueOf(d)!)}` : undefined}>{`Client · late, waiting ${a.when}`}</span> };
    if (a.late && a.who === "person") return { late: lateText(d, a.name ?? "Angel") };
    return a.late ? { late: text } : { meta: text };
}

export function NextRow({
    d,
    selected,
    onOpen,
    month = null,
    requests,
    metaOverride,
}: {
    d: Deliverable;
    selected: boolean;
    onOpen: () => void;
    month?: number | null;
    requests?: readonly ChangeRequest[] | null;
    /** Inside a due band: the band head says the date, so the row shows only a differing name, or nothing. */
    metaOverride?: string | null;
}) {
    const fact = metaOverride === undefined ? actorFact(d) : { meta: metaOverride ?? undefined };
    // Not started and not due yet: greyed. A client reply or a late date keeps it in ink.
    const idle = d.group === "needs_us" && d.clientWord === "Not started" && Boolean(d.actor) && d.actor!.when !== "today" && !d.actor!.late && !d.actor!.cameBack;
    return (
        <ListRow
            icon={<CodeLead d={d} />}
            lead="square"
            title={rowTitle(d, month)}
            sub={
                <span className={cn(idle ? "text-gray-400" : "text-gray-900")}>
                    {d.passedWithoutWork && (
                        <Tag tone="amber" className="mr-1.5">
                            No work logged
                        </Tag>
                    )}
                    {smallChangeLine(d, requests) ?? nextLine(d)}
                </span>
            }
            meta={fact.meta}
            late={fact.late}
            muted={idle || d.group === "later"}
            onClick={onOpen}
            stackMeta
            className={cn(selected && "bg-blue-50/60")}
            data-deliverable={d.key}
        />
    );
}

/** Who marked it Done by hand and when: "Marked done by Angel, 23 Oct". */
function forcedText(f: { by: string; at: string }): string {
    return `Marked done by ${f.by}, ${dayLabel(f.at)}`;
}

/**
 * Done by hand (#6463): staff mark an item, or the whole deliverable, Done when the work is finished
 * but Oura's trigger never fired. One click, nothing to type; it stays Done until Undo. The list
 * reloads from the change broadcast, so this only holds the press and its error, keyed by what was
 * pressed so an item's error shows on that item's row.
 */
function useForce() {
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<{ id: string; message: string } | null>(null);
    const press = async (id: string, write: () => Promise<{ ok: true } | DeliveryFailure>) => {
        setBusy(id);
        setError(null);
        const failed = "That did not go through. Try again.";
        try {
            const r = await write();
            if (!r.ok) setError({ id, message: r.message ?? failed });
        } catch {
            setError({ id, message: failed });
        } finally {
            setBusy(null);
        }
    };
    return { busy, error, press };
}

/**
 * The server that answers this Delivery knows Done by hand: it sends `forcedDone` (null when not
 * marked) on every item and deliverable. An older server sends no such field, and then the buttons
 * stay hidden rather than calling a route it does not have.
 */
function forceServed(d: Deliverable): boolean {
    // Where the tracker's tasks decide Done (#4682), Done is set in the sheet, not by hand here.
    if (d.noTasks === false) return false;
    return FORCE_DONE_ON && (d.forcedDone !== undefined || d.items.some((i) => i.forcedDone !== undefined));
}

function ItemForce({ businessId, item, force }: { businessId: string; item: DeliveryItem; force: ReturnType<typeof useForce> }) {
    const f = item.forcedDone;
    if (f && !f.whole) {
        return (
            <span className={cn(TYPE.tm, "flex shrink-0 items-center gap-1.5 text-gray-500")}>
                <span>{forcedText(f)}</span>
                <PillButton variant="text" busy={force.busy === item.id} disabled={force.busy !== null} onClick={() => void force.press(item.id, () => untickItem(businessId, item.id))}>
                    Undo
                </PillButton>
            </span>
        );
    }
    if (item.state === "done") return null;
    return (
        <PillButton size="sm" icon={CheckIcon} className="shrink-0" busy={force.busy === item.id} disabled={force.busy !== null} aria-label={`Mark ${item.label} done`} onClick={() => void force.press(item.id, () => forceItemDone(businessId, item.id))}>
            Done
        </PillButton>
    );
}

function DeliverableForce({ businessId, d, force }: { businessId: string; d: Deliverable; force: ReturnType<typeof useForce> }) {
    if (d.forcedDone) {
        return (
            <div className={cn(TYPE.tm, "flex items-center gap-1.5 text-gray-500")}>
                <span>{`${d.code}: ${forcedText(d.forcedDone)}`}</span>
                <PillButton variant="text" busy={force.busy === d.key} disabled={force.busy !== null} onClick={() => void force.press(d.key, () => reopenDeliverable(businessId, d.key, "Undid Marked done"))}>
                    Undo
                </PillButton>
            </div>
        );
    }
    if (d.group === "done") return null;
    return (
        <PillButton size="sm" icon={CheckIcon} busy={force.busy === d.key} disabled={force.busy !== null} onClick={() => void force.press(d.key, () => forceDeliverableDone(businessId, d.key))}>
            {`Mark ${d.code} done`}
        </PillButton>
    );
}

/** Where tasks decide Done (#4682): how many tasks are Done, a check that disagrees with the sheet, or no tasks at all. */
function TaskLines({ d }: { d: Deliverable }) {
    if (d.noTasks === undefined) return null;
    return (
        <div className="mb-1.5 space-y-1 pl-11 pr-2">
            {d.noTasks === false && d.taskCount !== undefined && (
                <p className={cn(TYPE.tm, "m-0 text-gray-600")}>
                    {d.tasksDone ?? 0} of {d.taskCount} tasks done in the tracker sheet
                </p>
            )}
            {d.noTasks === true && <p className={cn(TYPE.tm, "m-0 text-gray-500")}>No tasks in the tracker sheet carry {d.code}: Oura's checks decide it.</p>}
            {d.checkDisagrees && (
                <p role="status" className={cn(TYPE.tm, "m-0 text-rose-700")}>
                    The sheet says Done; Oura's check still fails: {d.checkDisagrees.said}
                </p>
            )}
        </div>
    );
}

function ChecklistRow({ businessId, d, selected, onOpen, month, metaOverride }: { businessId: string; d: Deliverable; selected: boolean; onOpen: () => void; month: number; metaOverride?: string | null }) {
    const fact = metaOverride === undefined ? actorFact(d) : { meta: metaOverride ?? undefined };
    const force = useForce();
    const served = forceServed(d);
    return (
        <div data-deliverable-block={d.key} className={cn("relative", selected && "rounded-xl bg-blue-50/60")}>
            <ListRow icon={<CodeLead d={d} />} lead="square" title={rowTitle(d, month)} meta={fact.meta} late={fact.late} onClick={onOpen} stackMeta data-deliverable={d.key} />
            <TaskLines d={d} />
            <ul className="m-0 mb-2 list-plain space-y-1.5 p-0 pl-11 pr-2">
                {d.items.map((item) => {
                    const sq = squareOf(item);
                    const isNext = d.next?.itemId === item.id;
                    return (
                        <li key={item.id} className={cn(TYPE.tc, "flex items-start gap-2")}>
                            <Square state={sq} className="mt-0.5" />
                            <span className="min-w-0 flex-1">
                                <span className={cn(sq === "done" ? "text-gray-500" : "text-gray-900")}>{item.label}</span>
                                <span className={cn("ml-1.5", sq === "came_back" ? "text-rose-600" : "text-gray-500")}>· {itemFact(item)}</span>
                            </span>
                            {isNext && <span className={cn(TYPE.tm, "shrink-0 rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-700")}>next</span>}
                            {served && (
                                <span className="flex shrink-0 flex-col items-end gap-0.5">
                                    <ItemForce businessId={businessId} item={item} force={force} />
                                    {force.error?.id === item.id && (
                                        <span role="alert" className={cn(TYPE.tm, "text-rose-600")}>
                                            {force.error.message}
                                        </span>
                                    )}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
            {served && (
                <div className="mb-3 flex flex-wrap items-center gap-2 pl-11 pr-2">
                    <DeliverableForce businessId={businessId} d={d} force={force} />
                    {force.error?.id === d.key && (
                        <span role="alert" className={cn(TYPE.tm, "text-rose-600")}>
                            {force.error.message}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}
